import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { useTheme } from "../context/ThemeContext";
import { LogOut, Monitor, Building2, ChevronDown, ChevronRight, Sun, Moon, AlertTriangle, Home, Menu, ArrowLeftRight, Wallet, Users } from "lucide-react";
import { http } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../context/SettingsContext";
import { NAV_GROUPS, applyNavOrder, isMenuAllowed, ALL_NAV_ITEMS } from "../lib/navConfig";
import CapitalRequestBell from "./CapitalRequestBell.jsx";
import HQArrivalsBell from "./HQArrivalsBell.jsx";
import TransferNotificationBell from "./TransferNotificationBell.jsx";
import ValasRequestBell from "./ValasRequestBell.jsx";
import { BookClosingBell } from "./BookClosingReminder.jsx";
import AvatarDialog, { UserAvatar } from "./AvatarDialog.jsx";
import { BrandLogo } from "./BrandLogo";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent } from "./ui/sheet";
import InstallPrompt from "./InstallPrompt.jsx";
import NotificationOptIn from "./NotificationOptIn.jsx";
import NotificationHub from "./NotificationHub.jsx";
import LiveClock from "./LiveClock.jsx";
import { useMemorial, MemorialRibbon } from "../lib/memorial";

function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="theme-toggle"
      title={isDark ? "Mode Normal (terang)" : "Mode Malam (gelap)"}
      aria-label="Ganti mode tampilan"
      className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white/60 text-slate-600 hover:bg-slate-100 transition-transform duration-150 active:scale-90"
    >
      <span key={isDark ? "sun" : "moon"} className="theme-icon-anim inline-flex">
        {isDark ? <Sun size={16} className="text-amber-400" /> : <Moon size={16} className="text-slate-500" />}
      </span>
    </button>
  );
}

const ICON_TONES = [
  { bg: "bg-emerald-100", fg: "text-emerald-600" },
  { bg: "bg-sky-100", fg: "text-sky-600" },
  { bg: "bg-violet-100", fg: "text-violet-600" },
  { bg: "bg-amber-100", fg: "text-amber-600" },
  { bg: "bg-rose-100", fg: "text-rose-600" },
  { bg: "bg-teal-100", fg: "text-teal-600" },
  { bg: "bg-indigo-100", fg: "text-indigo-600" },
  { bg: "bg-orange-100", fg: "text-orange-600" },
  { bg: "bg-fuchsia-100", fg: "text-fuchsia-600" },
  { bg: "bg-cyan-100", fg: "text-cyan-600" },
];
const toneFor = (key) => ICON_TONES[[...String(key)].reduce((a, c) => a + c.charCodeAt(0), 0) % ICON_TONES.length];

function NavGroup({ group, items, badges = {}, onNavigate }) {
  const [open, setOpen] = useState(!group.collapsible);
  return (
    <div className="pt-3 first:pt-0" data-testid={`nav-group-${group.title.toLowerCase().replace(/\s+/g, "-")}`}>
      <button type="button" onClick={() => group.collapsible && setOpen((o) => !o)}
        data-testid={`nav-group-toggle-${group.title.toLowerCase().replace(/\s+/g, "-")}`}
        className={`w-full flex items-center justify-between px-3 pb-1 text-[10px] uppercase tracking-widest text-slate-400 ${group.collapsible ? "hover:text-slate-700 cursor-pointer" : "cursor-default"}`}>
        <span>{group.title}</span>
        {group.collapsible && (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
      </button>
      {open && items.map(({ to, label, hint, icon: Icon, tid }) => {
        const badge = badges[to] || 0;
        const tone = toneFor(to);
        return (
        <NavLink key={to} to={to} data-testid={tid} onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition
             ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
          {({ isActive }) => (
            <>
              <span className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isActive ? "bg-white/15 text-white" : `${tone.bg} ${tone.fg}`}`}>
                <Icon size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block leading-tight">{label}</span>
                {hint && <span className={`block text-[10px] leading-tight mt-0.5 ${isActive ? "text-slate-300" : "text-slate-400"}`}>{hint}</span>}
              </span>
              {badge > 0 && (
                <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid={`nav-badge-${tid}`} title={`${badge} butuh perhatian`}>
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </>
          )}
        </NavLink>
        );
      })}
    </div>
  );
}

const BOTTOM_NAV = [
  { to: "/admin/dashboard", label: "Beranda", icon: Home },
  { to: "/admin/transaksi", label: "Transaksi", icon: ArrowLeftRight },
  { to: "/admin/saldo-rupiah", label: "Saldo", icon: Wallet },
  { to: "/admin/nasabah", label: "Nasabah", icon: Users },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const memorial = useMemorial();
  const memoActive = !!memorial?.active;
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const { visibleBranches, activeBranch, setBranch, canSwitch, currentBranch, branches } = useBranch();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const hqBranch = (branches || []).find((b) =>
    ["PUSAT", "KP", "MC1", "MC 1"].includes((b.code || "").toUpperCase()) ||
    /(MC 1|Pusat)/i.test(b.name || "")
  );
  const isAtHq = activeBranch === "all" || (hqBranch && (currentBranch?.id === hqBranch.id));
  const navigate = useNavigate();
  const location = useLocation();

  // Logout dengan kendali shift: jika pengaturan aktif & kasir masih punya shift
  // terbuka, logout diblokir sampai shift ditutup (khusus role kasir).
  const handleLogout = async () => {
    if (settings?.block_logout_with_open_shift && user?.role === "kasir") {
      try {
        const { data } = await http.get("/shifts/current");
        if (data && data.status === "open") {
          toast.error("Tutup shift dulu sebelum keluar", {
            description: "Shift Anda masih terbuka. Silakan lakukan Tutup Shift terlebih dahulu.",
            action: { label: "Buka Shift", onClick: () => navigate("/admin/shift") },
          });
          return;
        }
      } catch (e) { /* abaikan error cek shift, lanjutkan logout */ }
    }
    await logout();
    navigate("/login");
  };


  useEffect(() => {
    const path = location.pathname;
    const known = ALL_NAV_ITEMS.find((i) => i.to === path);
    if (known && !isMenuAllowed(user, path)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [location.pathname, user, navigate]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const [navCfg, setNavCfg] = useState(null);
  useEffect(() => {
    http.get("/settings/nav-order").then((r) => setNavCfg(r.data)).catch(() => {});
  }, []);
  const groups = useMemo(() => applyNavOrder(NAV_GROUPS, navCfg), [navCfg]);

  const [compliancePending, setCompliancePending] = useState(0);
  const [lowIdr, setLowIdr] = useState(0);
  const [lowValas, setLowValas] = useState(0);
  const [marginCount, setMarginCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    const fetchIt = () => {
      http.get("/compliance/summary").then((r) => { if (active) setCompliancePending(r.data?.needs_review || 0); }).catch(() => {});
      http.get("/cash-balances/low").then((r) => {
        if (!active) return;
        const list = r.data || [];
        setLowIdr(list.filter((s) => s.code === "IDR").length);
        setLowValas(list.filter((s) => s.code !== "IDR").length);
      }).catch(() => {});
      http.get("/margin-alerts").then((r) => { if (active) setMarginCount((r.data || []).length); }).catch(() => {});
    };
    fetchIt();
    const t = setInterval(fetchIt, 60000);
    return () => { active = false; clearInterval(t); };
  }, [isAdmin, location.pathname]);
  const navBadges = useMemo(() => ({
    "/admin/kepatuhan": compliancePending,
    "/admin/saldo-rupiah": lowIdr,
    "/admin/stok-valas": lowValas,
    "/admin/rates": marginCount,
  }), [compliancePending, lowIdr, lowValas, marginCount]);

  // Brand + branch switcher block, shared by desktop sidebar & mobile drawer.
  const brandBlock = (
    <div className={`px-6 py-6 border-b ${memoActive ? "border-blue-900/40" : "border-slate-200"}`}
      style={memoActive ? { background: "linear-gradient(160deg,#0b1e46,#152a5e)" } : undefined}>
      <div className="flex items-center gap-2 flex-wrap">
        <BrandLogo size="md" variant={memoActive ? "dark" : "light"} className="hidden lg:block" />
        {memoActive && (
          <span className="text-sm font-black tracking-tight text-white leading-none" data-testid="memorial-brand-label">
            {memorial.label}
          </span>
        )}
      </div>
      <div className="mt-2 hidden lg:flex items-center gap-2">
        <span className="inline-block h-1 w-6 rounded-full" style={{ background: memoActive ? memorial.accent : "#F6EE23" }} />
        <div className={`text-[10px] uppercase tracking-widest ${memoActive ? "text-blue-200" : "text-slate-500"}`}>PT Emerald Valasindo</div>
      </div>
      {memoActive && (
        <div className="mt-2 text-[9px] leading-snug text-blue-200/80" data-testid="memorial-brand-note"
          title={`${memorial.founder} · Lahir ${memorial.born} · Wafat ${memorial.died}`}>
          {memorial.note}: {memorial.founder}
        </div>
      )}
      <div className="mt-4">
        {canSwitch ? (() => {
          const homeBranchId = user?.branch_id;
          const offDefault = !!(activeBranch && homeBranchId && activeBranch !== homeBranchId);
          const isAggregate = activeBranch === "all";
          return (
            <div data-testid="branch-switcher-wrap">
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[9px] font-bold uppercase tracking-[0.2em] ${offDefault ? "text-amber-600" : "text-slate-400"}`}>
                  {offDefault ? "Sedang Melihat" : "Cabang Aktif"}
                </span>
                {offDefault && homeBranchId && (
                  <button type="button" onClick={() => setBranch(homeBranchId)} data-testid="branch-return-home"
                    className="inline-flex items-center gap-1 text-[9px] font-semibold text-amber-700 hover:text-amber-900 transition-colors">
                    <Home size={10} /> Kembali
                  </button>
                )}
              </div>
              <Select value={activeBranch || ""} onValueChange={setBranch}>
                <SelectTrigger data-testid="branch-switcher"
                  className={`h-auto py-2 rounded-lg transition-all whitespace-normal [&>span]:line-clamp-none [&>span]:w-full ${offDefault
                    ? "border-amber-400 bg-amber-50 ring-2 ring-amber-200/70 shadow-sm"
                    : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <SelectValue placeholder="Pilih cabang">
                    <span className="flex items-center gap-2.5 text-left w-full">
                      <span className={`relative w-8 h-8 rounded-md flex items-center justify-center shrink-0 text-white ${offDefault ? "bg-amber-500" : isAggregate ? "bg-indigo-600" : "bg-slate-900"}`}>
                        <Building2 size={15} />
                        {offDefault && (
                          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500 border border-white" />
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-bold leading-tight truncate">
                          {isAggregate ? "Semua Cabang" : (currentBranch ? currentBranch.name : "Pilih...")}
                        </span>
                        <span className={`block text-[10px] leading-tight truncate ${offDefault ? "text-amber-700 font-semibold" : "text-slate-400"}`}>
                          {offDefault
                            ? <span className="inline-flex items-center gap-1"><AlertTriangle size={9} /> Bukan cabang utama Anda</span>
                            : (isAggregate ? "Tampilan agregat" : (currentBranch ? `Kode ${currentBranch.code}` : "—"))}
                        </span>
                      </span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && <SelectItem value="all">📊 Semua Cabang (agregat)</SelectItem>}
                  {visibleBranches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.code} — {b.name}{b.id === homeBranchId ? " ★" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })() : currentBranch ? (
          <div className="flex items-center gap-2 text-xs bg-slate-100 rounded-md px-3 py-2" data-testid="branch-badge">
            <Building2 size={12} /> {currentBranch.code} — {currentBranch.name}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs bg-slate-100 rounded-md px-3 py-2 text-slate-400" data-testid="branch-badge-loading">
            <Building2 size={12} /> Memuat cabang...
          </div>
        )}
      </div>
    </div>
  );

  const navBody = (onNavigate) => (
    <nav className="flex-1 px-3 py-4 overflow-y-auto">
      {groups.map((g) => {
        const items = g.items.filter((n) => {
          if (n.adminOnly && !isAdmin) return false;
          if (n.superAdminOnly && !isSuperAdmin) return false;
          if (n.hqOnly && !isAtHq) return false;
          if (!isMenuAllowed(user, n.to)) return false;
          return true;
        });
        if (items.length === 0) return null;
        return <NavGroup key={g.title} group={g} items={items} badges={navBadges} onNavigate={onNavigate} />;
      })}
      <div className="pt-4 mt-4 border-t border-slate-200">
        <a
          href="/papan-harga" target="_blank" rel="noopener noreferrer"
          data-testid="nav-papan-harga"
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          <span className="shrink-0 w-7 h-7 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Monitor size={16} /></span><span><span className="block leading-tight">Papan Harga</span><span className="block text-[10px] text-slate-400">Layar harga untuk pelanggan</span></span>
        </a>
      </div>
    </nav>
  );

  const bottomItems = BOTTOM_NAV.filter((i) => isMenuAllowed(user, i.to));

  return (
    <div className="h-screen overflow-hidden bg-slate-50 text-slate-900 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col">
        {brandBlock}
        {navBody()}
      </aside>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-[85%] max-w-xs p-0 flex flex-col lg:hidden" data-testid="mobile-nav-drawer">
          {brandBlock}
          {navBody(() => setDrawerOpen(false))}
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0 overflow-hidden flex flex-col">
        <header className="shrink-0 z-20 flex items-center gap-2 sm:gap-3 px-4 sm:px-6 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-slate-200 bg-white shadow-sm">
          {/* Mobile hamburger + compact brand mark */}
          <div className="flex items-center gap-2.5 mr-auto lg:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Buka menu"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 active:scale-90 transition"
            >
              <Menu size={18} />
            </button>
            <img
              src="/favicon-192.png"
              alt="Emerald Money Changer"
              data-testid="mobile-brand-mark"
              className="h-8 w-8 rounded-full object-contain select-none shadow-sm"
              draggable="false"
            />
          </div>
          <div className="hidden lg:block flex-1" aria-hidden="true" />
          <ThemeToggle />
          <span className="hidden md:inline-flex"><LiveClock /></span>
          <div className="hidden lg:flex items-center gap-1.5">
            <BookClosingBell onCount={(n) => setCounts((c) => (c.book === n ? c : { ...c, book: n }))} />
            <CapitalRequestBell onCount={(n) => setCounts((c) => (c.capital === n ? c : { ...c, capital: n }))} />
            <HQArrivalsBell onCount={(n) => setCounts((c) => (c.hq === n ? c : { ...c, hq: n }))} />
            <ValasRequestBell onCount={(n) => setCounts((c) => (c.valas === n ? c : { ...c, valas: n }))} />
            <TransferNotificationBell onCount={(n) => setCounts((c) => (c.transfer === n ? c : { ...c, transfer: n }))} />
          </div>
          <div className="lg:hidden">
            <NotificationHub counts={counts} isAdmin={isAdmin} isHqUser={isAdmin && isAtHq} needsClosing={(counts.book || 0) > 0} />
          </div>
          <div className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-slate-200">
            <UserAvatar user={user} size={32} onClick={() => setAvatarOpen(true)} />
            <AvatarDialog open={avatarOpen} onOpenChange={setAvatarOpen} />
            <div className="hidden md:block min-w-0 max-w-[140px]">
              <div className="text-xs font-semibold truncate leading-tight" data-testid="header-user-name">{user?.name}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide leading-tight">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="hidden lg:inline-flex h-9 gap-1.5 border-slate-300 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 transition"
            data-testid="btn-logout-top"
            onClick={handleLogout}
            title="Keluar"
          >
            <LogOut size={14} />
            <span>Keluar</span>
          </Button>
        </header>
        <div id="app-scroll" className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden pb-20 lg:pb-0">
          <MemorialRibbon m={memorial} />
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 inset-x-0 z-30 lg:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgb(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5">
          {bottomItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} data-testid={`bottom-nav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition
                 ${isActive ? "text-slate-900" : "text-slate-400"}`}>
              {({ isActive }) => (
                <>
                  <span className={`flex h-7 w-10 items-center justify-center rounded-full transition ${isActive ? "bg-[#F6EE23] text-slate-900" : ""}`}>
                    <Icon size={18} />
                  </span>
                  <span className="leading-none">{label}</span>
                </>
              )}
            </NavLink>
          ))}
          <button type="button" onClick={handleLogout} data-testid="bottom-nav-logout"
            className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium text-slate-400 hover:text-rose-600 transition">
            <span className="flex h-7 w-10 items-center justify-center rounded-full"><LogOut size={18} /></span>
            <span className="leading-none">Keluar</span>
          </button>
        </div>
      </nav>

      <InstallPrompt />
      <NotificationOptIn />
    </div>
  );
}
